import { useId, useState } from "react";
import { clsx } from "clsx";
import { ChevronRight } from "lucide-react";
import { SubventionInlineDetail } from "@/features/subventions/components/feed/SubventionInlineDetail";
import type { EligibleSubsidy } from "@/features/subventions/types/subvention.types";
import {
  displayName, envelopeName, expiryLabel, type RemainingFigure, remainingFigure,
  stripLeadingEmoji, type SubsidyStatus, statusOf, tileFor,
} from "@/features/subventions/utils/subventionStatus";
import { universeIcon } from "@/features/subventions/utils/universeActions";

type SubventionFeedRowProps = {
  subsidy: EligibleSubsidy
  onOpenDetail: (subsidy: EligibleSubsidy) => void
  // Rows rendered inside an envelope drop the parent chip — the folder header already names it.
  insideEnv?: boolean
};

const TONE_TEXT = {
  ok: "text-success",
  warn: "text-warning-emphasis",
  depleted: "text-error-emphasis",
} as const;

const TAG_STYLE = {
  urgent: "bg-lighterror text-error-emphasis",
  soon: "bg-lightwarning text-warning-emphasis",
} as const;

// The row's right edge: the urgency tag, the remaining figure (or "Détail" when no figure shows),
// and the expand chevron.
const RowTrailing = ({
  status, figure, showFigure, expiry, open,
}: {
  status: SubsidyStatus
  figure: RemainingFigure
  showFigure: boolean
  expiry: string | null
  open: boolean
}) => (
  <span className="flex shrink-0 items-center gap-3">
    {status.tag && (
      <span className={clsx("rounded-full px-2 py-0.5 text-[10px] font-bold", TAG_STYLE[status.tag.cls])}>
        {status.tag.text}
      </span>
    )}
    <span className="text-right">
      {showFigure
        ? (
          <>
            <span className={clsx("block text-[15px] font-extrabold tabular-nums tracking-[-0.01em]", TONE_TEXT[status.tone])}>
              {figure.primary}
            </span>
            {figure.secondary && (
              <span className="block text-[11px] tabular-nums text-gray-400 dark:text-gray-500">{figure.secondary}</span>
            )}
            {expiry && (
              <span className="block text-[10.5px] text-gray-400 dark:text-gray-500">{expiry}</span>
            )}
          </>
        )
        : <span className="block text-[13px] font-bold text-gray-500 dark:text-gray-400">Détail</span>}
    </span>
    <ChevronRight
      size={16}
      className={clsx("text-gray-400 transition-transform motion-reduce:transition-none", open && "rotate-90")}
      aria-hidden
    />
  </span>
);

RowTrailing.displayName = "RowTrailing";

export const SubventionFeedRow = ({ subsidy, onOpenDetail, insideEnv = false }: SubventionFeedRowProps) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  const status = statusOf(subsidy);
  const figure = remainingFigure(subsidy);
  const showFigure = Boolean(subsidy.display_limit_label && figure.primary);
  // Hide the parent chip inside an envelope; envelopeName already drops the V1 migration root.
  const env = envelopeName(subsidy);
  const showEnv = !insideEnv && env !== null;
  const snippet = (subsidy.summary ?? "").split("•")[0].trim();
  const expiry = status.tag ? null : expiryLabel(status);
  const depleted = status.tone === "depleted";

  return (
    <div
      className={clsx(
        "rounded-2xl border border-gray-200 bg-white transition-shadow motion-reduce:transition-none dark:border-gray-700 dark:bg-gray-800",
        open ? "shadow-md" : "hover:shadow-sm",
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center gap-3.5 rounded-2xl px-4 py-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/60"
      >
        {/* Dim only the tile + name on a depleted row; the figure keeps its own contrast. */}
        <span
          aria-hidden
          className={clsx(
            "flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl bg-gray-100 text-xl dark:bg-gray-700",
            depleted && "opacity-60",
          )}
        >
          {tileFor(subsidy, universeIcon)}
        </span>

        <span className={clsx("min-w-0 flex-1", depleted && "opacity-60")}>
          <span className="block text-[14.5px] font-bold leading-tight text-gray-900 dark:text-white">
            {displayName(subsidy)}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
            {showEnv && (
              <span className="rounded-full border border-gray-200 bg-gray-100 px-2 py-0.5 text-[10.5px] font-semibold text-gray-600 dark:border-gray-700 dark:bg-gray-700 dark:text-gray-300">
                {stripLeadingEmoji(env)}
              </span>
            )}
            {snippet && <span>{snippet}</span>}
          </span>
        </span>

        <RowTrailing status={status} figure={figure} showFigure={showFigure} expiry={expiry} open={open} />
      </button>

      <div id={panelId}>
        {open && <SubventionInlineDetail subsidy={subsidy} onOpenDetail={onOpenDetail} />}
      </div>
    </div>
  );
};

SubventionFeedRow.displayName = "SubventionFeedRow";
